import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { AnswerComponent, AnswersContainer, ButtonContainer, CustomAnswerTextAreaContainer, SubmitButton } from './styles';
import GologinModal from '../../../../ui/gologin-modal';
import { GologinNewCheckbox } from '../../../../ui/gologin-checkbox/gologin-new-checkbox';
import { GologinTextArea } from '../../../../ui/gologin-textarea/styles';
import { message } from 'antd';
import { getEnTranslationByKeyString, keepOnlyLettersAndDigits } from '../../../../../common/utils';
import { sendActionAnalytics } from '../../api';
import { E_ANALYTICS_ACTIONS } from '../../../../../common/constants/analytics';

const answersKeysMap = {
  firstAnswer: 'Found another solution',
  secondAnswer: 'I was blocked when I used GoLogin',
  thirdAnswer: `I didn't know it was a subscription, I thought it was a one-time fee`,
  forthAnswer: 'Expensive',
  customAnswer: 'My own version',
};

type CancelSubscriptionQuizModalProps = {
  isVisible: boolean;
  hideModal: () => void;
  unsubscribe?: () => Promise<void>;
};

const CancelSubscriptionQuizModal: React.FC<CancelSubscriptionQuizModalProps> = (props: CancelSubscriptionQuizModalProps) => {
  const { isVisible, hideModal, unsubscribe } = props;

  const [selectedAnswerKey, setSelectedAnswerKey] = useState<string>();
  const [customAnswer, setCustomAnswer] = useState<string>('');

  const { t } = useTranslation();

  const isNeedBlockUnsubscription =
    (!selectedAnswerKey && !customAnswer) || (selectedAnswerKey === answersKeysMap.customAnswer && !customAnswer);

  const updateCustomAnswer = (answer: string) => {
    if (selectedAnswerKey !== answersKeysMap.customAnswer) {
      setSelectedAnswerKey(answersKeysMap.customAnswer);
    }

    setCustomAnswer(answer);
  };

  const renderCustomAnswerTextArea = () => {
    return (
      <CustomAnswerTextAreaContainer>
        <GologinTextArea
          width={'100%'}
          height={'90px'}
          style={{ whiteSpace: 'normal' }}
          value={customAnswer}
          onChange={(event) => updateCustomAnswer(event.target.value)}
          placeholder={t`Enter your answer...` || ''}
          rows={4}
          maxLength={1000}
        />
      </CustomAnswerTextAreaContainer>
    );
  };

  const onSendClicked = () => {
    if (isNeedBlockUnsubscription) {
      message.error(<Trans i18nKey={'Please provide a reason for canceling your subscription'} />);

      return;
    }

    const isNeedToWriteDefinedAnswer = selectedAnswerKey && selectedAnswerKey !== answersKeysMap.customAnswer;
    const answer = isNeedToWriteDefinedAnswer
      ? getEnTranslationByKeyString(selectedAnswerKey)
      : keepOnlyLettersAndDigits(customAnswer);

    if (!answer) {
      return;
    }

    unsubscribe && unsubscribe().then(() => {
      sendActionAnalytics(E_ANALYTICS_ACTIONS.subscriptionCancellationReason, { actionInfo: answer });
      message.success(<Trans i18nKey={'Your subscription has been successfully canceled'} />);
    });

    closeModal();
  };

  const closeModal = () => {
    setSelectedAnswerKey('');
    setCustomAnswer('');
    hideModal();
  };

  return (
    <GologinModal
      isVisible={isVisible}
      onClose={closeModal}
      title={ 
        <div style={{ width: 345 }}>
          <Trans i18nKey={'Can you please tell us why you decided to cancel your subscription?'} />
        </div>
      }
    >
      <AnswersContainer>
        {Object.values(answersKeysMap).map((answerValueKey) => {
          return (
            <AnswerComponent>
              <GologinNewCheckbox
                innerPadding={'2px 0px'}
                onChange={() => setSelectedAnswerKey(answerValueKey)}
                checked={selectedAnswerKey === answerValueKey}
              />
              <div onClick={() => setSelectedAnswerKey(answerValueKey)} style={{ cursor: 'pointer' }}>
                <Trans i18nKey={answerValueKey} />
              </div>
            </AnswerComponent>
          )
        })}
      </AnswersContainer>
      {renderCustomAnswerTextArea()}
      <ButtonContainer>
        <SubmitButton
          style={{ background: isNeedBlockUnsubscription ? 'var(--A8ABAE)' : 'var(--00B86D)'}}
          disabled={isNeedBlockUnsubscription}
          onClick={onSendClicked}
        >
          <Trans i18nKey='Send' />
        </SubmitButton>
      </ButtonContainer>
    </GologinModal>
  );
};

export default CancelSubscriptionQuizModal;
